<template>
  <article class="flex w-full" data-component="CardEducation">
    <AtomLink :url="url" class="group grid md:grid-cols-12 w-full rounded-3xl overflow-hidden shadow-lg hover:shadow-none transform transition duration-200">
      <figure class="row-span-1 md:col-span-6 relative">
        <span
          v-if="labels.length"
          class="absolute z-10 bg-app-secondary-7-violet--purple-heart top-0 left-0 rounded-br-3xl text-white font-semibold text-sm lg:text-lg leading-none px-5 py-3 lg:px-8 lg:py-3"
        >
          <template v-for="label in labels">
            {{ label }}
          </template>
        </span>
        <MoleculePicture class="block w-full h-full object-cover group-hover:scale-110 group-focus:scale-110 transform transition duration-200" :image="image" />
      </figure>
      <section class="c-content relative row-span-1 md:col-span-6 flex">
        <div class="relative z-20 bg-white flex flex-1 flex-col justify-end px-5 pb-5 lg:pl-2 md:pb-8" :class="{ 'md:pt-8': body, 'md:pt-12 lg:pt-16': !body }">
          <div class="flex-auto">
            <p v-if="season" class="text-app-secondary-7-violet--purple-heart font-medium text-sm sm:text-base lg:text-lg" :class="{ 'c-content__title': season }">{{ season }}</p>
            <h3
              class="mr-10 md:mr-5 text-base sm:text-lg lg:text-2xl xl:text-3xl font-medium leading-tight text-app-secondary-7-violet--purple-heart mb-2"
              :class="{ 'md:mb-3': body, 'md:mb-5': !body, 'c-content__title': !season }"
            >
              {{ title }}
            </h3>
            <ul class="flex flex-wrap text-sm lg:text-base xl:text-lg font-medium leading-tight mb-4" :class="{ 'lg:mb-6': !body }">
              <template v-if="location">
                <li class="py-1">
                  <span class="mr-3 lg:mr-5">{{ location }}</span>
                </li>
              </template>
              <template v-if="duration">
                <li v-if="location" role="presentation" class="my-1 border-r border-solid border-app-secondary-7-violet--purple-heart mr-3 lg:mr-5"></li>
                <li class="py-1">
                  <span class="mr-3 lg:mr-5">{{ duration }}</span>
                </li>
              </template>
              <template v-if="price">
                <li v-if="location || duration" role="presentation" class="my-1 border-r border-solid border-app-secondary-7-violet--purple-heart mr-3 lg:mr-5"></li>
                <li class="py-1">
                  <span class="mr-3 lg:mr-5">{{ price }}</span>
                </li>
              </template>
            </ul>
          </div>
          <footer class="flex flex-wrap">
            <template v-if="url">
              <MoleculeButton class="mr-2 lg:hidden" background-color="bg-app-secondary-7-violet--purple-heart group-hover:bg-black" size="medium">
                {{ $t('education.card-education.information-and-order') }}
              </MoleculeButton>
              <MoleculeButton class="mr-4 hidden lg:flex" background-color="bg-app-secondary-7-violet--purple-heart group-hover:bg-black">
                {{ $t('education.card-education.information-and-order') }}
              </MoleculeButton>
            </template>
          </footer>
        </div>
      </section>
    </AtomLink>
  </article>
</template>

<script setup lang="ts">
defineProps({
  body: { type: String, required: false, default: undefined },
  image: {
    type: Object as PropType<Record<string, any>>,
    required: false,
    default: () => ({ _smUrl: '/images/fallback/345x206.png', _smWidth: 908, _smHeight: 545, _mdUrl: '/images/fallback/463x280.png', _mdWidth: 947, _mdHeight: 573 }),
  },
  imgAlt: { type: String, required: false, default: undefined },
  labels: { type: Array as PropType<any[]>, required: false, default: () => [] },
  location: { type: String, required: false, default: undefined },
  price: { type: String, required: false, default: undefined },
  duration: { type: String, required: false, default: undefined },
  title: { type: String, required: true },
  season: { type: String, required: false, default: undefined },
  url: { type: String, required: true },
})

setupTranslationPrefixer('CardEventAndSeries')
</script>

<style lang="scss" scoped>
$side-size-svg: 49px;

.v-leading-small {
  line-height: 1.15;
}

.c-content {
  @apply z-10;
  padding-top: calc(#{$side-size-svg} - 1px);
  margin-top: calc(-1 * (#{$side-size-svg} - 1px));

  @screen md {
    @apply mt-0 pt-0;
  }

  &::before {
    content: '';
    background-image: url('/images/brand/BrandLyreCardHorizontal.svg');
    width: 84px;
    height: $side-size-svg;
    @apply absolute top-0 right-0 bg-no-repeat bg-cover;

    @screen md {
      @apply right-auto left-0;
      background-image: url('/images/brand/BrandLyreCardVertical.svg');
      width: $side-size-svg;
      height: 84px;
      padding-left: calc(#{$side-size-svg} - 1px);
      margin-left: calc(-1 * (#{$side-size-svg} - 1px));
    }
  }

  &::after {
    content: '';
    width: calc(100% - 84px + 1px);
    height: $side-size-svg;
    @apply absolute top-0 left-0 bg-white;

    @screen md {
      @apply top-auto bottom-0;
      width: $side-size-svg;
      height: calc(100% - 84px);
      margin-left: calc(-1 * (#{$side-size-svg} - 1px));
    }
  }
}

.c-content__title {
  margin-top: calc(-1 * theme('spacing.6'));

  @screen md {
    @apply mt-0;
  }
}
</style>
